import { useId } from "react";
import AddItemInput from "./AddItemInput/AddItemInput";
import DropdownInput from "./DropdownInput/DropdownInput";
import MoneyInput from "./MoneyInput/MoneyInput";
import NumberPicker from "./NumberPicker/NumberPicker";
import TextareaInput from "./TextareaInput/TextareaInput";
import TextAndColorInput from "./TextAndColorInput/TextAndColorInput";
import createSlotProps from "../slotProps";
import css from "./Input.module.css";

/**
 * Native attributes apply to the field. `styles` augments each named slot;
 * `classes` replaces that slot's default classes (including with "").
 * Special behavior is configured through dropdown, addItem, and numberPicker.
 * addItem.lockedItems lists existing chips that cannot be removed.
 * Money and number-picker controls share a container border and focus ring.
 * textAndColor keeps value/onChange for text and colorValue/onColorChange
 * for the picker (native change events with target.value as #rrggbb).
 * defaultColorValue initializes an uncontrolled picker; colorLabel names it.
 */
export default function Input({
	id,
	type = "text",
	label,
	styles = {},
	classes = {},
	dropdown,
	addItem,
	numberPicker,
	colorValue,
	defaultColorValue,
	onColorChange,
	colorLabel,
	children,
	...nativeProps
}) {
	const generatedId = useId();
	const inputProps = { ...nativeProps, id: id || generatedId };
	const slotProps = createSlotProps(styles, classes);
	const controlProps = slotProps("control", css.control);
	let control;

	switch (type) {
		case "dropdown":
			control = (
				<DropdownInput inputProps={inputProps} styles={styles} classes={classes} config={dropdown}>
					{children}
				</DropdownInput>
			);
			break;
		case "textarea":
			control = <TextareaInput inputProps={inputProps} controlProps={controlProps} slotProps={slotProps} />;
			break;
		case "addItem":
			control = <AddItemInput inputProps={inputProps} controlProps={controlProps} slotProps={slotProps} config={addItem} />;
			break;
		case "money":
			control = <MoneyInput inputProps={inputProps} slotProps={slotProps} numberPicker={numberPicker} />;
			break;
		case "textAndColor":
			control = (
				<TextAndColorInput
					inputProps={inputProps}
					slotProps={slotProps}
					colorValue={colorValue}
					defaultColorValue={defaultColorValue}
					onColorChange={onColorChange}
					colorLabel={colorLabel || (typeof label === "string" ? `${label} color code` : "Color code")}
				/>
			);
			break;
		default:
			control = (
				<div {...(numberPicker ? slotProps("control", css.containedControl) : controlProps)}>
					<input {...inputProps} type={type} {...slotProps("field", numberPicker ? css.containedField : css.field)} />
					{numberPicker && <NumberPicker config={numberPicker} slotProps={slotProps} inputProps={inputProps} />}
				</div>
			);
	}

	return (
		<div {...slotProps("root", css.root)}>
			{label && (
				<label htmlFor={inputProps.id} {...slotProps("label", css.label)}>
					{label}
					{inputProps.required && <span {...slotProps("requiredIndicator", css.requiredIndicator)}>*</span>}
				</label>
			)}
			{control}
		</div>
	);
}
